/**
 * The option lists behind the report filter drawers — one loader per list, replacing five
 * copies of fetchPortalFilters and three near-identical fetch*FilterOptions
 * (docs/DUPLICATION_AUDIT.md, B14).
 */

import { apiFetch, postReport } from '../services/api.js';
import { getCampusConfig, getAllVendors } from '../services/session.js';
import { fetchReasonCodeOptions } from './reasonCodeOptions.js';
import { NURSE_CALL_CATEGORY_FILTER } from './deviceCategories.js';
import { convertSecto12Hour, luxonParseSQLDate, luxonCompareDateTimes } from '../libs.jsx';
import { fetchCheckinWindows, fetchRoundsWindows } from '../pages/settings/wellness-settings/api/wellnessSettingsApi.js';
import { ALERT_REPORT_SECTIONS } from '../components/v2/reportFilterSections.js';

/* group → [request flag, response key]. getportalfilters only checks each flag for
   presence (any non-empty string includes the group), which is why the old copies' '0'
   and '1' for area_filter behaved the same. */
const PORTAL_GROUPS = {
  areas: ['area_filter', 'areas'],
  areaGroups: ['area_group_filter', 'area_groups'],
  areaTypes: ['area_type_filter', 'area_types'],
  residents: ['resident_filter', 'residents'],
  residentGroups: ['resident_group_filter', 'resident_groups'],
  users: ['user_filter', 'users'],
  userGroups: ['user_group_filter', 'user_groups'],
  deviceCategories: ['device_category_filter', 'device_categories'],
};
export const ALL_PORTAL_GROUPS = Object.keys(PORTAL_GROUPS);

/**
 * Areas, groups, residents, users and device categories for a campus (getportalfilters).
 * Residents gain `resident_name` (first + last), which the drawers display.
 *
 * @param {number} campusId
 * @param {string[]} [groups] — which lists to load (default: all)
 * @returns {Promise<object>} one array per requested group; {} if the request fails
 */
export async function fetchPortalFilters(campusId, groups = ALL_PORTAL_GROUPS) {
  const body = { rep_type: 'getportalfilters', campus_id: campusId };
  Object.entries(PORTAL_GROUPS).forEach(([group, [flag]]) => { body[flag] = groups.includes(group) ? '1' : ''; });
  const res = await postReport(body, '[reportFilterOptions] fetchPortalFilters error');
  if (res.ErrorCode) return {};
  const out = {};
  groups.forEach((group) => { out[group] = res.data?.[PORTAL_GROUPS[group][1]] || []; });
  if (out.residents) {
    out.residents = out.residents.map((r) => ({
      ...r,
      resident_name: `${r.resident_first_name || ''} ${r.resident_last_name || ''}`.trim() || '(unknown)',
    }));
  }
  return out;
}

/**
 * Event types, sorted by name: att_event_types comes back in table order and is the
 * longest list in the drawer (FilterSubSection renders options as received). Executive
 * and Environmental did not sort.
 */
export async function fetchEventTypeOptions(campusId) {
  const rows = await apiFetch({ table: 'att_event_types', procedure: 'fetchall', reqType: 'direct', pKey: 'campus_id', pVal: campusId });
  return [...(rows || [])].sort((a, b) =>
    (a.event_type_name || '').localeCompare(b.event_type_name || '', undefined, { sensitivity: 'base' }));
}

/* att_event_types.event_category_id 2 = maintenance. Maintenance events are stored apart
   from alert events, so a maintenance type never matches a row in the alert-family
   reports, and an alert type never matches one in the 4.0 Maintenance report. Compared as
   a number: the column can arrive as a string. A type with no category counts as an
   alert type. */
export const MAINTENANCE_EVENT_CATEGORY_ID = 2;
const isMaintenanceEventType = (t) => Number(t?.event_category_id) === MAINTENANCE_EVENT_CATEGORY_ID;

/** Event types the 4.0 Maintenance report can match (filterOptions.maintenanceEventTypes). */
export function maintenanceEventTypes(eventTypes) {
  return (eventTypes || []).filter(isMaintenanceEventType);
}

/* The alert-family list also leaves out Concierge (3) and Non-Critical (4) types, and
   offers a feature's types only where the community has that feature. */
const HIDDEN_ALERT_EVENT_CATEGORY_IDS = [3, 4];
const ROUNDS_EVENT_TYPE_IDS = [15, 19];                   // Rounds, Missed Rounds
const CHECKIN_EVENT_TYPE_IDS = [16, 18];                  // Resident Check In, Missed Check-in
const INOVONICS_CLOUD_EVENT_TYPE_IDS = [36, 37, 38, 39];  // Unit / Unit Schedule / Unit Transmitter / Unit Transmitter Schedule Breach
export const INOVONICS_CLOUD_VENDOR_ID = 14;

/**
 * The community features that decide which alert event types are offered, read the way
 * the nav reads them: Staff Rounds and Resident Check-in reports (is_rounding,
 * is_resident_checkin) and the RTLS page (an active vendor 14, Inovonics Cloud).
 *
 * @param {number|string} [campusId] — default: the current campus
 * @returns {{ rounds: boolean, checkin: boolean, inovonicsCloud: boolean }}
 */
export function eventTypeFeatures(campusId) {
  const cfg = getCampusConfig(campusId);
  return {
    rounds: Boolean(cfg?.is_rounding),
    checkin: Boolean(cfg?.is_resident_checkin),
    inovonicsCloud: getAllVendors(campusId).some((v) => v?.vendor_active && Number(v?.vendor_id) === INOVONICS_CLOUD_VENDOR_ID),
  };
}

/** Event types the alert-family reports offer (filterOptions.alertEventTypes): Alert
    Details, Hourly, Executive, Caregiver, Shift. No maintenance, Concierge or Non-Critical
    types, and Rounds / Check-in / Inovonics Cloud breach types only with that feature
    (eventTypeFeatures; a feature left out of `features` counts as off). */
export function alertEventTypes(eventTypes, features = {}) {
  const hiddenTypeIds = new Set([
    ...(features.rounds ? [] : ROUNDS_EVENT_TYPE_IDS),
    ...(features.checkin ? [] : CHECKIN_EVENT_TYPE_IDS),
    ...(features.inovonicsCloud ? [] : INOVONICS_CLOUD_EVENT_TYPE_IDS),
  ]);
  return (eventTypes || []).filter((t) => !isMaintenanceEventType(t)
    && !HIDDEN_ALERT_EVENT_CATEGORY_IDS.includes(Number(t?.event_category_id))
    && !hiddenTypeIds.has(Number(t?.event_type_id)));
}

/**
 * Event type ids with the ones this drawer hides taken out. A selection can predate the
 * narrower lists (a restored run, a Run Report Local, a stored schedule), or a community
 * feature can since have been turned off. A hidden type has no checkbox to clear it, so it
 * drops out. Ids the full list does not know are kept, and nothing changes until the
 * options have loaded.
 *
 * @param {Array}  ids           — selected event_type_ids
 * @param {object} filterOptions — { eventTypes (all), alertEventTypes, maintenanceEventTypes, … }
 * @param {Array}  [sections]    — the drawer's preset; its Event Types sub names the shown list.
 *        A preset without Event Types (cloud Maintenance) leaves the ids alone.
 * @returns {Array} the same array when nothing was dropped
 */
export function shownEventTypeIds(ids, filterOptions, sections = ALERT_REPORT_SECTIONS) {
  const sub = sections.flatMap((s) => s.subs).find((d) => d.key === 'eventTypes');
  const all = filterOptions?.eventTypes;
  const shown = sub && filterOptions?.[sub.optionsKey];
  if (!Array.isArray(ids) || ids.length === 0 || !Array.isArray(all) || !Array.isArray(shown)) return ids;
  const shownIds = new Set(shown.map((t) => String(t.event_type_id)));
  const hidden = new Set(all.map((t) => String(t.event_type_id)).filter((id) => !shownIds.has(id)));
  const kept = ids.filter((id) => !hidden.has(String(id)));
  return kept.length === ids.length ? ids : kept;
}

/** shownEventTypeIds over a page's reportFilters; the same object when nothing changed. */
export function dropHiddenEventTypes(filters, filterOptions, sections) {
  const ids = filters?.eventTypes;
  const kept = shownEventTypeIds(ids, filterOptions, sections);
  return kept === ids ? filters : { ...filters, eventTypes: kept };
}

/** A window as a filter option: its times as the subtext, and "Inactive" while it is ignored
    (the report leaves ignored windows out, so a schedule on one stays empty until it's
    re-enabled). Same Active / Inactive test as the Wellness Settings tables. */
function windowOption(w) {
  const times = `${convertSecto12Hour(w.window_start)} – ${convertSecto12Hour(w.window_end)}`;
  const active = luxonCompareDateTimes(luxonParseSQLDate(w.ignore_until), 'lt');
  return { ...w, window_times: active ? times : `${times} · Inactive` };
}

/**
 * Check-in and rounds windows for the report windows filter (docs/CHECKIN_WINDOW_FILTER_PLAN.md):
 * `checkinWindows` / `roundsWindows` feed each report's drawer, and `windows` (both) lets a
 * stored schedule's chips name either type. Each list is sorted by start time.
 *
 * @returns {Promise<{ checkinWindows: object[], roundsWindows: object[], windows: object[] }>}
 */
export async function fetchWindowOptions(campusId) {
  const [checkin, rounds] = await Promise.all([
    fetchCheckinWindows(campusId).catch(() => []),
    fetchRoundsWindows(campusId).catch(() => []),
  ]);
  const prep = (rows) => (Array.isArray(rows) ? rows : [])
    .map(windowOption)
    .sort((a, b) => (a.window_start ?? 0) - (b.window_start ?? 0));
  const checkinWindows = prep(checkin);
  const roundsWindows = prep(rounds);
  return { checkinWindows, roundsWindows, windows: [...checkinWindows, ...roundsWindows] };
}

/** Nurse-call devices for the Devices filter (the categories the report procs cover). */
export async function fetchDeviceOptions(campusId) {
  const rows = await apiFetch({
    procedure: 'sp_dev_getInovonicsDevices',
    reqType: 'stored',
    payload: { device_filter: '', device_category_filter: NURSE_CALL_CATEGORY_FILTER, campus_id: campusId },
  });
  return rows || [];
}

/**
 * The full option set — Alert Details, Hourly, Executive, Caregiver, Shift, Maintenance and
 * the scheduled-report editor. Reason codes include the synthetic "No Reason" row. Event
 * types come three ways: all, alert-only and maintenance-only (the drawers show one of the
 * last two, by preset).
 *
 * @returns {Promise<object>} {} if any request throws
 */
export async function fetchFilterOptions(campusId) {
  try {
    const [portal, eventTypes, reasonCodes, devices] = await Promise.all([
      fetchPortalFilters(campusId),
      fetchEventTypeOptions(campusId),
      fetchReasonCodeOptions(campusId),
      fetchDeviceOptions(campusId),
    ]);
    return {
      areas: portal.areas || [],
      areaGroups: portal.areaGroups || [],
      areaTypes: portal.areaTypes || [],
      residents: portal.residents || [],
      residentGroups: portal.residentGroups || [],
      users: portal.users || [],
      userGroups: portal.userGroups || [],
      deviceCategories: portal.deviceCategories || [],
      devices,
      /* eventTypes stays the full list: stored schedules and chips name types from it. */
      eventTypes,
      alertEventTypes: alertEventTypes(eventTypes, eventTypeFeatures(campusId)),
      maintenanceEventTypes: maintenanceEventTypes(eventTypes),
      reasonCodes,
    };
  } catch (err) {
    console.error('[reportFilterOptions] fetchFilterOptions error:', err);
    return {};
  }
}
